'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { plansApi, PlanType, type UserPlanResponse, type PlanResponse } from '@/api/plans';

interface PlansContextValue {
  userPlan: UserPlanResponse | null;
  availablePlans: PlanResponse[];
  planType: PlanType;
  proPlanId: string | undefined;
  isLoading: boolean;
  refreshPlans: () => Promise<void>;
}

const PlansContext = createContext<PlansContextValue | undefined>(undefined);

export function PlansProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [userPlan, setUserPlan] = useState<UserPlanResponse | null>(null);
  const [availablePlans, setAvailablePlans] = useState<PlanResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  const refreshPlans = useCallback(async () => {
    try {
      setIsLoading(true);
      const [myPlan, allPlans] = await Promise.all([
        plansApi.getMyPlan(),
        plansApi.getAll(),
      ]);
      setUserPlan(myPlan);
      setAvailablePlans(allPlans);
    } catch (error) {
      console.error('Error loading plans:', error);
      setUserPlan(null);
      setAvailablePlans([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      hasFetchedRef.current = false;
      setUserPlan(null);
      setAvailablePlans([]);
      return;
    }
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    refreshPlans();
  }, [isAuthenticated, user?.id, refreshPlans]);

  const planType = userPlan?.type ?? PlanType.FREE;
  const proPlanId = availablePlans.find((p) => p.type === PlanType.PRO)?.id;

  return (
    <PlansContext.Provider
      value={{
        userPlan,
        availablePlans,
        planType,
        proPlanId,
        isLoading,
        refreshPlans,
      }}
    >
      {children}
    </PlansContext.Provider>
  );
}

export function usePlans() {
  const context = useContext(PlansContext);
  if (context === undefined) {
    throw new Error('usePlans must be used within a PlansProvider');
  }
  return context;
}
